import React from "react";
import styled from "styled-components";
import { Input } from "../shared/Input";
import { MenuCard } from "../menu/MenuCard";
import { observer } from "mobx-react-lite";
import { managementStore } from "../../stores/ManagementStore";
import { Dropdown } from "../editor/Dropdown";
import { useTranslation } from "react-i18next";
import { EditorComplexity } from "../../../shared/definitions/other/EditorComplexity";
import { allEditorComplexities } from "../../stores/LocalSettingsStore";
import { useHistory } from "react-router";
import { openProductionEditor } from "./WorkshopHelper";
import { AccessCode } from "./AccessCode";
import { moduleParticipantAccessCodeConfig, standaloneModulePlayCodeConfig } from "../../../shared/helper/accesscodeHelpers";
import { getPublicGameVariantLink } from "../../data/routes";
const PropertyView = styled(MenuCard).withConfig({
  displayName: "ModuleDetailView__PropertyView",
  componentId: "sc-1bpdaxj-0"
})(["grid-column-start:2;"]);
const PropertyContainer = styled.table.withConfig({
  displayName: "ModuleDetailView__PropertyContainer",
  componentId: "sc-1bpdaxj-1"
})(["background:white;padding:3px;margin:3px;th{min-width:130px;font-weight:normal;vertical-align:top;padding:5px 0px;}"]);
const ErrorText = styled.div.withConfig({
  displayName: "ModuleDetailView__ErrorText",
  componentId: "sc-1bpdaxj-2"
})(["color:red;"]);
const MapSelectorDropdown = styled(Dropdown).withConfig({
  displayName: "ModuleDetailView__MapSelectorDropdown",
  componentId: "sc-1bpdaxj-3"
})(["max-width:310px;"]);
export const ModuleDetailView = observer(props => {
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const selectedModule = managementStore.getModule(props.editItem);

  function updateName(newName) {
    selectedModule.setName(newName);
  }

  function updateDescription(newDescription) {
    selectedModule.setDescription(newDescription);
  }

  function updateStartDate(newStartDate) {
    selectedModule.setStartDate(HTMLDateValueToDateObject(newStartDate));
  }

  function updateEndDate(newEndDate) {
    selectedModule.setEndDate(HTMLDateValueToDateObject(newEndDate));
  }

  function updateStartAct(newStartAct) {
    selectedModule.setStartAtAct(newStartAct);
  }

  function toggleIsStandaloneModule() {
    selectedModule.setIsStandalone(!selectedModule.isStandalone);
  }

  function updateStandaloneModuleStartMapId(newStandaloneMapId) {
    selectedModule.setStandaloneMapId(newStandaloneMapId);
  }

  function HTMLDateValueToDateObject(dateString) {
    if (dateString.length == 0) return 0;
    return new Date(dateString).getTime();
  }

  function dateObjectToHTMLDateInputValue(dateNumber) {
    if (!dateNumber) return undefined;
    return new Date(dateNumber).toISOString().split('T')[0];
  }

  async function openModuleInEditor(moduleId) {
    openProductionEditor(history, moduleId);
  }

  const hasStartAndEndDate = Boolean(selectedModule.startDate && selectedModule.endDate);
  const moduleMapList = managementStore.getModuleMapList(selectedModule.$modelId);
  return /*#__PURE__*/React.createElement(React.Fragment, null, selectedModule && /*#__PURE__*/React.createElement(PropertyView, null, /*#__PURE__*/React.createElement(PropertyContainer, null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_name")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Input, {
    value: selectedModule.name,
    onChange: ({
      target
    }) => updateName(target.value)
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_description")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Input, {
    value: selectedModule.description,
    onChange: ({
      target
    }) => updateDescription(target.value)
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_start_and_end_date")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Input, {
    type: "date",
    value: dateObjectToHTMLDateInputValue(selectedModule.startDate) || "",
    onChange: ({
      target
    }) => updateStartDate(target.value),
    error: !selectedModule.startDate
  }), /*#__PURE__*/React.createElement(Input, {
    type: "date",
    value: dateObjectToHTMLDateInputValue(selectedModule.endDate) || "",
    onChange: ({
      target
    }) => updateEndDate(target.value),
    error: !selectedModule.endDate
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_ready_to_play")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: selectedModule.readyToPlay,
    onChange: () => selectedModule.setReadyToPlay(!selectedModule.readyToPlay)
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_visible_in_public_menu")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: selectedModule.visibleInPublicMenu,
    onChange: () => selectedModule.setVisibleInPublicMenu(!selectedModule.visibleInPublicMenu),
    disabled: !selectedModule.readyToPlay
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.highlight_module")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: selectedModule.highlighted,
    onChange: () => selectedModule.setHighlighted(!selectedModule.highlighted)
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_complexity")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Dropdown, {
    value: selectedModule.editorComplexity,
    onChange: e => selectedModule.setEditorComplexity(+e.target.value)
  }, allEditorComplexities.map(complexity => {
    if (complexity != EditorComplexity.Production) {
      return /*#__PURE__*/React.createElement("option", {
        key: complexity,
        value: complexity
      }, t("editor.local_setting_complexity_" + complexity));
    }

    return null;
  })))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_start_act")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Dropdown, {
    disabled: selectedModule.isStandalone,
    value: selectedModule.startAtAct,
    onChange: e => updateStartAct(+e.target.value)
  }, /*#__PURE__*/React.createElement("option", {
    key: 1,
    value: 1
  }, t("action_editor.property_act"), " 1"), /*#__PURE__*/React.createElement("option", {
    key: 2,
    value: 2
  }, t("action_editor.property_act"), " 2"), /*#__PURE__*/React.createElement("option", {
    key: 3,
    value: 3
  }, t("action_editor.property_act"), " 3"), /*#__PURE__*/React.createElement("option", {
    key: 4,
    value: 4
  }, t("action_editor.property_act"), " 4"), /*#__PURE__*/React.createElement("option", {
    key: 5,
    value: 5
  }, t("action_editor.property_act"), " 5")), selectedModule.isStandalone && /*#__PURE__*/React.createElement("div", null, t("management.module_start_act_standalone_note")))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_access_code")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(AccessCode, {
    value: selectedModule.accesscode,
    setValue: selectedModule.setAccesscode.bind(selectedModule),
    config: moduleParticipantAccessCodeConfig
  }), hasStartAndEndDate && /*#__PURE__*/React.createElement("div", null, t("management.module_access_code_description")), !hasStartAndEndDate && /*#__PURE__*/React.createElement(ErrorText, null, t("management.module_missing_start_or_end_date")))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_standalone")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: selectedModule.isStandalone,
    onChange: e => toggleIsStandaloneModule()
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.choose_standalone_map_id")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(MapSelectorDropdown, {
    disabled: !selectedModule.isStandalone,
    value: selectedModule.standaloneMapId,
    onChange: e => updateStandaloneModuleStartMapId(+e.target.value),
    className: selectedModule.isStandalone && !moduleMapList.some(map => map.id === selectedModule.standaloneMapId) ? "invalid" : undefined
  }, /*#__PURE__*/React.createElement("option", {
    key: 0,
    value: 0
  }, t("shared.no_map_selected")), managementStore.getModuleMapList(selectedModule.$modelId).map(moduleMapData => /*#__PURE__*/React.createElement("option", {
    key: moduleMapData.id,
    value: moduleMapData.id
  }, "(ID#", moduleMapData.id, ") ", moduleMapData.name))))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, t("management.module_standalone_play_code")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(AccessCode, {
    value: selectedModule.standalonePlayCode,
    setValue: selectedModule.setStandalonePlayCode.bind(selectedModule),
    config: standaloneModulePlayCodeConfig,
    disabled: !selectedModule.isStandalone
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("a", {
    href: getPublicGameVariantLink([selectedModule.$modelId])
  }, t("management.module_public_link")))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
    onClick: () => openModuleInEditor(selectedModule.$modelId)
  }, t("management.open_module_in_editor"))))))));
});